'use client';

import { ReactNode, Suspense } from 'react';
import dynamic from 'next/dynamic';
import Navbar from '@/components/layout/Navbar';
import MobileNav from '@/components/layout/MobileNav';
import Footer from '@/components/layout/Footer';
import ScrollProgress from '@/components/ui/ScrollProgress';

// Decorative / interaction-only clients — not needed for SSR/SEO, gate behind ssr:false
// so their JS is deferred off the critical path and never blocks hydration.
const MouseBackground = dynamic(() => import('@/components/ui/MouseBackground'), { ssr: false });
const MouseInteractions = dynamic(() => import('@/components/ui/MouseInteractions'), { ssr: false });
const CursorFollower = dynamic(() => import('@/components/ui/CursorFollower'), { ssr: false });
// FloatingButtons only render after scroll > 300px — deferring its code is safe.
const FloatingButtons = dynamic(() => import('@/components/layout/FloatingButtons'), { ssr: false });
// Pixel tracker — fires POST /api/analytics on every route change. ssr:false
// keeps it off the SSR critical path and prevents Suspense boundary issues
// from useSearchParams during build.
const PixelTracker = dynamic(() => import('@/components/analytics/PixelTracker'), { ssr: false });

export default function MainLayout({ children }: { children: ReactNode }) {
  // NOTE: The previous implementation wrapped `children` in an
  // <AnimatePresence mode="wait" key={pathname}> with exit animations.
  // That fights the App Router's client-side navigation: mode="wait"
  // blocks the new page from rendering until the old page finishes
  // its exit animation, which looks like a full page reload — especially
  // noticeable when navigating back to the async server-component home
  // route, where the fresh RSC payload is momentarily hidden behind the
  // outgoing exit animation. Per-section entrance animations still run
  // inside each page (framer-motion whileInView etc.).
  return (
    <>
      <MouseBackground />
      <MouseInteractions />
      <CursorFollower />
      <ScrollProgress />
      <Navbar />
      <MobileNav />
      <main className="pt-20 flex-1">{children}</main>
      <Footer />
      <FloatingButtons />
      <Suspense fallback={null}>
        <PixelTracker />
      </Suspense>
    </>
  );
}
